/**
 * THE central keyboard-shortcut registry (CLAUDE.md §12).
 *
 * This module owns the ONE `window` keydown listener in the app. Screens
 * register shortcuts declaratively with `useShortcut(...)`; they must never
 * attach their own listener. `npm run lint` fails the build if any file under
 * `src/` outside this one calls addEventListener('keydown').
 *
 * Reserved keys — do not reassign:
 *   ?   the shortcuts help overlay (this module owns it)
 *   /   focus the search box
 *   Esc close the topmost overlay
 */
import * as Dialog from '@radix-ui/react-dialog';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toPersianDigits } from './format.js';

const ShortcutContext = createContext(null);

/** Typing in a field must not trigger single-key shortcuts. */
function isEditableTarget(target) {
  if (!target) return false;
  if (target.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/**
 * A modal dialog owns the keyboard while it is open.
 *
 * Without this, a screen's shortcut fires *underneath* an open dialog. The
 * concrete bug it fixes: the contact profile registers Escape to go back to the
 * list, so pressing Escape to dismiss the interaction composer both closed the
 * dialog AND navigated away — the user lost the profile they were on for
 * cancelling a form. Radix calls preventDefault on its own Escape handling, but
 * that does not stop this listener, so the registry has to defer explicitly.
 *
 * Detected from the DOM rather than from a counter, because Radix owns the
 * dialogs' open state and a counter would be a second source of truth that
 * drifts the first time someone unmounts a dialog without closing it.
 */
function isModalOpen() {
  return document.querySelector('[role="dialog"][data-state="open"]') !== null;
}

/** Normalize an event to a comparable key string: "?", "/", "mod+k", "Escape". */
function eventKey(event) {
  const parts = [];
  if (event.ctrlKey || event.metaKey) parts.push('mod');
  if (event.altKey) parts.push('alt');
  if (event.shiftKey && event.key.length > 1) parts.push('shift');
  parts.push(event.key);
  return parts.join('+');
}

export function ShortcutProvider({ children }) {
  // Map<key, {handler, description, group, allowInInput}> — a ref so that
  // registering does not re-run the listener effect.
  const registry = useRef(new Map());
  const [helpOpen, setHelpOpen] = useState(false);
  const [, forceRender] = useState(0);

  const register = useCallback((key, entry) => {
    registry.current.set(key, entry);
    forceRender((n) => n + 1); // the help overlay lists what is registered
    return () => {
      registry.current.delete(key);
      forceRender((n) => n + 1);
    };
  }, []);

  // The app's single keydown listener.
  useEffect(() => {
    function onKeyDown(event) {
      const key = eventKey(event);

      // The help overlay is itself a dialog, so it answers for its own keys
      // before the modal guard below would swallow them.
      if (helpOpen) {
        if (key === 'Escape' || (key === '?' && !isEditableTarget(event.target))) {
          setHelpOpen(false);
        }
        return;
      }

      // Any other open dialog owns the keyboard: no screen shortcut fires
      // underneath it, and `?` does not stack the help overlay on top of a form.
      if (isModalOpen()) return;

      if (key === '?' && !isEditableTarget(event.target)) {
        event.preventDefault();
        setHelpOpen(true);
        return;
      }

      const entry = registry.current.get(key);
      if (!entry) return;
      if (isEditableTarget(event.target) && !entry.allowInInput) return;
      event.preventDefault();
      entry.handler(event);
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [helpOpen]);

  const value = useMemo(
    () => ({ register, registry, openHelp: () => setHelpOpen(true) }),
    [register],
  );

  return (
    <ShortcutContext.Provider value={value}>
      {children}
      <ShortcutHelpOverlay
        open={helpOpen}
        onOpenChange={setHelpOpen}
        entries={[...registry.current.entries()]}
      />
    </ShortcutContext.Provider>
  );
}

/**
 * Register a shortcut for as long as the calling component is mounted.
 *
 * @param key         "?" | "/" | "mod+k" | "Escape" | …
 * @param handler     called with the event
 * @param options     { description, group, allowInInput, enabled }
 */
export function useShortcut(key, handler, options = {}) {
  const context = useContext(ShortcutContext);
  if (!context) throw new Error('useShortcut must be used inside <ShortcutProvider>');

  const { description = '', group = 'عمومی', allowInInput = false, enabled = true } = options;
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return undefined;
    return context.register(key, {
      handler: (event) => handlerRef.current(event),
      description,
      group,
      allowInInput,
    });
  }, [context, key, description, group, allowInInput, enabled]);
}

export function useShortcutHelp() {
  const context = useContext(ShortcutContext);
  return context?.openHelp ?? (() => {});
}

/* ---------- the help overlay (reserved: `?`) ---------- */

const KEY_LABELS = { mod: '⌘/Ctrl', alt: 'Alt', shift: 'Shift', Escape: 'Esc' };

function KeyCap({ combo }) {
  return (
    <span className="flex gap-1">
      {combo.split('+').map((part) => (
        <kbd
          key={part}
          className="rounded border border-border bg-surface-hover px-2 py-1 text-xs font-medium"
        >
          {KEY_LABELS[part] ?? toPersianDigits(part)}
        </kbd>
      ))}
    </span>
  );
}

function ShortcutHelpOverlay({ open, onOpenChange, entries }) {
  const groups = entries.reduce((acc, [key, entry]) => {
    (acc[entry.group] ??= []).push([key, entry]);
    return acc;
  }, {});

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-text/30" />
        <Dialog.Content
          dir="rtl"
          className="fixed left-1/2 top-1/2 w-overlay -translate-x-1/2 -translate-y-1/2
                     rounded border border-border bg-surface p-6 shadow-overlay"
        >
          <Dialog.Title className="text-lg font-bold text-text">میان‌برهای صفحه‌کلید</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-text-muted">
            برای بستن، کلید <kbd className="font-medium">Esc</kbd> یا دوباره{' '}
            <kbd className="font-medium">؟</kbd> را بزنید.
          </Dialog.Description>

          <div className="mt-5 flex flex-col gap-5">
            {Object.entries(groups).map(([group, groupEntries]) => (
              <section key={group}>
                <h3 className="mb-2 text-sm font-medium text-text-muted">{group}</h3>
                <ul className="flex flex-col gap-2">
                  {groupEntries.map(([key, entry]) => (
                    <li key={key} className="flex items-center justify-between gap-4">
                      <span className="text-base text-text">{entry.description || key}</span>
                      <KeyCap combo={key} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}

            <section>
              <h3 className="mb-2 text-sm font-medium text-text-muted">همیشه در دسترس</h3>
              <ul className="flex flex-col gap-2">
                <li className="flex items-center justify-between gap-4">
                  <span className="text-base text-text">نمایش همین راهنما</span>
                  <KeyCap combo="?" />
                </li>
              </ul>
            </section>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
